import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface MediaDeviceState {
  isCameraInUse: boolean;
  isMicrophoneInUse: boolean;
  cameraStream: MediaStream | null;
  microphoneStream: MediaStream | null;
  lastUsedBy: string | null;
  consumerCount: number;
}

export interface CaptureNeeds {
  video: boolean;
  audio: boolean;
}

interface ConsumerLease {
  videoTrack: MediaStreamTrack | null;
  audioTrack: MediaStreamTrack | null;
  micStream: MediaStream | null;
}

/**
 * Shared capture hub: one getUserMedia source, cloned tracks per consumer.
 * Classroom and AI chat can both use the mic; only classroom uses the camera.
 * Stopping a clone does not stop the source; source stops when no leases remain.
 */
@Injectable({
  providedIn: 'root',
})
export class MediaDeviceCoordinatorService {
  private mediaState$ = new BehaviorSubject<MediaDeviceState>({
    isCameraInUse: false,
    isMicrophoneInUse: false,
    cameraStream: null,
    microphoneStream: null,
    lastUsedBy: null,
    consumerCount: 0,
  });

  private sourceStream: MediaStream | null = null;
  private sourceVideoTrack: MediaStreamTrack | null = null;
  private sourceAudioTrack: MediaStreamTrack | null = null;
  private ensureInFlight: Promise<void> | null = null;
  private leases = new Map<string, ConsumerLease>();
  /** Fires consumer ids whose clones were stopped by a source re-acquire. */
  private leasesInvalidated$ = new Subject<string[]>();

  getMediaState(): Observable<MediaDeviceState> {
    return this.mediaState$.asObservable();
  }

  /** Subscribe to re-checkout after hub upgrades invalidate clones. */
  getLeasesInvalidated(): Observable<string[]> {
    return this.leasesInvalidated$.asObservable();
  }

  hasActiveCapture(): boolean {
    return !!this.sourceStream;
  }

  /**
   * Ensure a single shared capture matching the requested kinds.
   * Upgrades an existing audio-only capture to include video when needed.
   */
  async ensureCapture(needs: CaptureNeeds): Promise<void> {
    if (this.ensureInFlight) {
      await this.ensureInFlight;
    }

    const hasVideo = !!this.sourceVideoTrack && this.sourceVideoTrack.readyState === 'live';
    const hasAudio = !!this.sourceAudioTrack && this.sourceAudioTrack.readyState === 'live';

    if ((!needs.video || hasVideo) && (!needs.audio || hasAudio)) {
      return;
    }

    this.ensureInFlight = this._acquireOrUpgrade(needs).finally(() => {
      this.ensureInFlight = null;
    });
    await this.ensureInFlight;
  }

  /** Checkout a video track clone for LiveKit (or null if no camera). */
  async checkoutVideoTrack(consumerId: string): Promise<MediaStreamTrack | null> {
    await this.ensureCapture({ video: true, audio: true });
    if (!this.sourceVideoTrack || this.sourceVideoTrack.readyState !== 'live') {
      return null;
    }

    const lease = this._getOrCreateLease(consumerId);
    if (lease.videoTrack && lease.videoTrack.readyState === 'live') {
      return lease.videoTrack;
    }

    lease.videoTrack = this.sourceVideoTrack.clone();
    this._publishState(consumerId);
    return lease.videoTrack;
  }

  /** Checkout a mic MediaStream containing a cloned audio track. */
  async checkoutMicStream(consumerId: string): Promise<MediaStream> {
    await this.ensureCapture({ video: false, audio: true });
    if (!this.sourceAudioTrack || this.sourceAudioTrack.readyState !== 'live') {
      throw new Error('No microphone track available');
    }

    const lease = this._getOrCreateLease(consumerId);
    if (
      lease.micStream &&
      lease.audioTrack &&
      lease.audioTrack.readyState === 'live'
    ) {
      return lease.micStream;
    }

    // Fresh clone so mute via enabled on one consumer cannot silence another.
    if (lease.audioTrack) {
      try {
        lease.audioTrack.stop();
      } catch {
        /* ignore */
      }
    }

    lease.audioTrack = this.sourceAudioTrack.clone();
    lease.micStream = new MediaStream([lease.audioTrack]);
    this._publishState(consumerId);
    return lease.micStream;
  }

  /**
   * Release a consumer's clones. Stops source only when no leases remain.
   */
  release(consumerId: string): void {
    const lease = this.leases.get(consumerId);
    if (!lease) return;

    if (lease.audioTrack) {
      try {
        lease.audioTrack.stop();
      } catch {
        /* ignore */
      }
    }
    if (lease.videoTrack) {
      try {
        lease.videoTrack.stop();
      } catch {
        /* ignore */
      }
    }

    this.leases.delete(consumerId);
    console.log(`🛑 [MediaHub] Released consumer: ${consumerId}`, {
      remaining: this.leases.size,
    });

    if (this.leases.size === 0) {
      this._stopSource();
    } else {
      this._publishState(null);
    }
  }

  /** @deprecated Use ensureCapture + checkout* */
  async requestCamera(requesterId: string): Promise<MediaStream> {
    await this.ensureCapture({ video: true, audio: false });
    const track = await this.checkoutVideoTrack(requesterId);
    if (!track) throw new Error('No camera track available');
    return new MediaStream([track]);
  }

  /** @deprecated Use ensureCapture + checkoutMicStream */
  async requestMicrophone(requesterId: string): Promise<MediaStream> {
    return this.checkoutMicStream(requesterId);
  }

  /** @deprecated Use ensureCapture + checkout* */
  async requestCameraAndMicrophone(
    requesterId: string
  ): Promise<{ videoStream: MediaStream; audioStream: MediaStream }> {
    await this.ensureCapture({ video: true, audio: true });
    const videoTrack = await this.checkoutVideoTrack(requesterId);
    const audioStream = await this.checkoutMicStream(requesterId);
    if (!videoTrack) throw new Error('No camera track available');
    return {
      videoStream: new MediaStream([videoTrack]),
      audioStream,
    };
  }

  async releaseCamera(requesterId: string): Promise<void> {
    const lease = this.leases.get(requesterId);
    if (!lease?.videoTrack) return;
    try {
      lease.videoTrack.stop();
    } catch {
      /* ignore */
    }
    lease.videoTrack = null;
    if (!lease.audioTrack && !lease.micStream) {
      this.release(requesterId);
    } else {
      this._publishState(requesterId);
    }
  }

  async releaseMicrophone(requesterId: string): Promise<void> {
    const lease = this.leases.get(requesterId);
    if (!lease?.audioTrack && !lease?.micStream) return;
    if (lease.audioTrack) {
      try {
        lease.audioTrack.stop();
      } catch {
        /* ignore */
      }
    }
    lease.audioTrack = null;
    lease.micStream = null;
    if (!lease.videoTrack) {
      this.release(requesterId);
    } else {
      this._publishState(requesterId);
    }
  }

  async releaseAll(requesterId: string): Promise<void> {
    this.release(requesterId);
  }

  async checkMediaAvailability(): Promise<{ camera: boolean; microphone: boolean }> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return {
        camera: devices.some((d) => d.kind === 'videoinput'),
        microphone: devices.some((d) => d.kind === 'audioinput'),
      };
    } catch {
      return { camera: false, microphone: false };
    }
  }

  // ─── private ───────────────────────────────────────────

  private async _acquireOrUpgrade(needs: CaptureNeeds): Promise<void> {
    if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
      throw new Error('Camera/microphone access requires HTTPS or localhost.');
    }

    const wantVideo = needs.video;
    const wantAudio = needs.audio;
    const hasVideo = !!this.sourceVideoTrack && this.sourceVideoTrack.readyState === 'live';
    const hasAudio = !!this.sourceAudioTrack && this.sourceAudioTrack.readyState === 'live';

    // Need a full re-acquire when upgrading (e.g. audio-only → A/V).
    const needFull =
      (wantVideo && !hasVideo && hasAudio) ||
      (wantAudio && !hasAudio && hasVideo) ||
      (!hasVideo && !hasAudio);

    if (!needFull && hasVideo && hasAudio) {
      return;
    }

    const constraints: MediaStreamConstraints = {
      video: wantVideo || hasVideo
        ? {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 },
          }
        : false,
      audio:
        wantAudio || hasAudio
          ? {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            }
          : false,
    };

    console.log('🎥🎤 [MediaHub] ensureCapture', {
      wants: needs,
      hadVideo: hasVideo,
      hadAudio: hasAudio,
      constraints,
    });

    // Preserve existing live tracks of the kind we already have when only
    // adding the other kind via a second GUM is not reliable — re-acquire both.
    const prevLeases = [...this.leases.keys()];
    let invalidated: string[] = [];
    if (this.sourceStream && needFull) {
      // Stop old source; consumers must re-checkout clones after upgrade.
      // Always notify every prior lease id — even if tracks were already
      // dead — so AI re-inject cannot miss an A/V upgrade race.
      this._stopSourceTracksOnly();
      for (const id of prevLeases) {
        const lease = this.leases.get(id);
        if (!lease) continue;
        if (lease.audioTrack) {
          try {
            lease.audioTrack.stop();
          } catch {
            /* ignore */
          }
          lease.audioTrack = null;
          lease.micStream = null;
        }
        if (lease.videoTrack) {
          try {
            lease.videoTrack.stop();
          } catch {
            /* ignore */
          }
          lease.videoTrack = null;
        }
        invalidated.push(id);
      }
    }

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.sourceStream = stream;
    this.sourceVideoTrack = stream.getVideoTracks()[0] ?? null;
    this.sourceAudioTrack = stream.getAudioTracks()[0] ?? null;

    console.log('🎥🎤 [MediaHub] Capture ready', {
      video: !!this.sourceVideoTrack,
      audio: !!this.sourceAudioTrack,
      consumers: this.leases.size,
      invalidated,
    });

    this._publishState(null);
    if (invalidated.length) {
      this.leasesInvalidated$.next(invalidated);
    }
  }

  private _getOrCreateLease(consumerId: string): ConsumerLease {
    let lease = this.leases.get(consumerId);
    if (!lease) {
      lease = { videoTrack: null, audioTrack: null, micStream: null };
      this.leases.set(consumerId, lease);
    }
    return lease;
  }

  private _stopSourceTracksOnly(): void {
    if (this.sourceStream) {
      this.sourceStream.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch {
          /* ignore */
        }
      });
    }
    this.sourceStream = null;
    this.sourceVideoTrack = null;
    this.sourceAudioTrack = null;
  }

  private _stopSource(): void {
    console.log('🛑 [MediaHub] Stopping source capture (no consumers)');
    this._stopSourceTracksOnly();
    this._publishState(null);
  }

  private _publishState(lastUsedBy: string | null): void {
    const videoLive =
      !!this.sourceVideoTrack && this.sourceVideoTrack.readyState === 'live';
    const audioLive =
      !!this.sourceAudioTrack && this.sourceAudioTrack.readyState === 'live';

    this.mediaState$.next({
      isCameraInUse: videoLive,
      isMicrophoneInUse: audioLive,
      cameraStream:
        videoLive && this.sourceVideoTrack
          ? new MediaStream([this.sourceVideoTrack])
          : null,
      microphoneStream:
        audioLive && this.sourceAudioTrack
          ? new MediaStream([this.sourceAudioTrack])
          : null,
      lastUsedBy: lastUsedBy ?? this.mediaState$.value.lastUsedBy,
      consumerCount: this.leases.size,
    });
  }
}
